import axios, { AxiosResponse } from "axios";
import { SLEntriesError, FatalError } from "./ErrorHelpers";
import { IUserProviderState } from "../contexts/UserContext";

export const AuthorizationToken = async (getTokenPromiseFn: IUserProviderState["accessToken"]): Promise<string> => {
  const msalPayload = await getTokenPromiseFn();
  if (msalPayload) {
    return `Bearer ${msalPayload.accessToken}`;
  } else {
    return "";
  }
};

export interface IRequestConfig {
  timeout: number;
  params?: any;
}

export type tMethod = "get" | "delete" | "head" | "options" | "post" | "put" | "patch";
export const authRequest = async (
  url: string,
  method: tMethod,
  getTokenFn: IUserProviderState["accessToken"],
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  data?: any,
  requestCfg?: IRequestConfig
): Promise<AxiosResponse<any>> => {
  const timeout = requestCfg && requestCfg.timeout && 0;
  const Authorization = await AuthorizationToken(getTokenFn);
  let payload = {
    method,
    url,
    headers: {
      "Content-Type": "application/json",
      Authorization
    },
    timeout
  };
  if (data) {
    payload = Object.assign(payload, {
      data
    });
  }
  if (requestCfg && requestCfg.params) {
    payload = Object.assign(payload, {
      params: requestCfg.params
    });
  }
  try {
    const result = await axios(payload);
    return result;
  } catch (e) {
    if (e && e.response && e.response.data) {
      if (e.response.data.InternalError) {
        const error = e.response.data;
        if (error.InternalError.type === "UnAuthorized Access") {
          // show alert here, navigate to dashboard at top level
          alert(error.Message);
          throw new FatalError(error.Message as string);
        } else {
          throw new SLEntriesError([error.Message as string]);
        }
      } else {
        const possibleErrors = Array.isArray(e.response.data);
        if (possibleErrors && e.response.data.length !== 0 && e.response.data[0].errorMessage) {
          const errorMessages = e.response.data.map((anError: any) => anError.errorMessage);
          throw new SLEntriesError([errorMessages as string]);
        } else {
          throw e;
        }
      }
    } else {
      throw e;
    }
  }
  // don't return after a try-catch block
};

export const authRequestBlob = async (
  url: string,
  method: tMethod,
  getTokenFn: IUserProviderState["accessToken"],
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  data?: any
): Promise<AxiosResponse<any>> => {
  const Authorization = await AuthorizationToken(getTokenFn);
  let payload = {
    method,
    url,
    responseType: "blob",
    headers: {
      Authorization
    }
  };
  if (data) {
    payload = Object.assign(payload, {
      data
    });
  }
  return axios(payload as any);
};

export const authGetBlob = (
  url: string,
  getTokenFn: IUserProviderState["accessToken"]
): ReturnType<typeof authRequestBlob> => authRequestBlob(url, "get", getTokenFn);

export const authGet = (url: string, getTokenFn: IUserProviderState["accessToken"]): ReturnType<typeof authRequest> =>
  authRequest(url, "get", getTokenFn);
export const authPost = (
  url: string,
  getTokenFn: IUserProviderState["accessToken"],
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  data: any
): ReturnType<typeof authRequest> => authRequest(url, "post", getTokenFn, data);
export const authPatch = (
  url: string,
  getTokenFn: IUserProviderState["accessToken"],
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  data: any
): ReturnType<typeof authRequest> => authRequest(url, "patch", getTokenFn, data);
export const authDelete = (
  url: string,
  getTokenFn: IUserProviderState["accessToken"],
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  data: any
): ReturnType<typeof authRequest> => authRequest(url, "delete", getTokenFn, data);
